import React from 'react'
import {
    ChangeLogFieldsFragment,
    ClientPreviewFieldsFragment,
    CustomerSegmentPreviewFieldsFragment,
    UserFieldsFragment,
} from '~graphql/generated/graphql'
import SharedStack from '../wrappers/shared-stack'
import {
    getCleanEnum,
    getClientFullName,
    getHumanReadablePropName,
    getUserFullName,
} from '~utils/helpers'
import { DateFormat } from '~utils/shared-types'
import SharedLabel from '../form/shared-label'
import { useFormatDate } from '~utils/hooks/use-format-date'
import SharedInfoText from '../texts/shared-info-text'
import SharedHelpText from '../form/shared-help-text'
import MissingText from '../texts/missing-text'
import { format } from 'date-fns'
import {
    ChangeLogEntities,
    CLIENT_FIELD_KEYS,
    CUSTOMER_SEGMENT_FIELD_KEYS,
    USER_FIELD_KEYS,
} from './use-change-log-entities'
import {
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Box,
    Text,
    ModalCloseButton,
} from '@chakra-ui/react'
import ChangeLogHtmlItem from './change-log-html-item'

interface Props {
    changeLog: ChangeLogFieldsFragment
    changeLogEntities: ChangeLogEntities
}

function getChangeLogUser(changeLog: ChangeLogFieldsFragment): string {
    if (changeLog.user) {
        return `${changeLog.user.first_name} ${changeLog.user.last_name}`
    }
    return 'System'
}

export default function ChangeLogListItem({
    changeLog,
    changeLogEntities,
}: Props) {
    const { formatDate } = useFormatDate()
    const fields = Object.keys(changeLog.old_values ?? {})

    return (
        <SharedStack>
            <SharedHelpText>
                {formatDate(changeLog.created_at, DateFormat.DateAtTime)} by{' '}
                {getChangeLogUser(changeLog)}
            </SharedHelpText>
            <SharedStack>
                {fields.map(field => {
                    const oldValue = changeLog.old_values[field] ?? undefined
                    const newValue = changeLog.new_values[field] ?? undefined

                    return (
                        <SharedStack key={field} row align="flex-start">
                            <SharedLabel w="156px">
                                {getHumanReadablePropName(field).capitalize()}
                            </SharedLabel>
                            <SharedStack
                                row
                                align="flex-start"
                                justify="center"
                            >
                                {!!oldValue ? (
                                    <FormattedValue
                                        value={oldValue}
                                        field={field}
                                        changeLogEntities={changeLogEntities}
                                    />
                                ) : (
                                    <MissingText w="200px">
                                        No value
                                    </MissingText>
                                )}
                                <SharedLabel w="48px">→</SharedLabel>
                                {!!newValue ? (
                                    <FormattedValue
                                        value={newValue}
                                        field={field}
                                        changeLogEntities={changeLogEntities}
                                    />
                                ) : (
                                    <MissingText w="200px">
                                        No value
                                    </MissingText>
                                )}
                            </SharedStack>
                        </SharedStack>
                    )
                })}
            </SharedStack>
        </SharedStack>
    )
}

interface FormattedValueProps {
    value: unknown
    field: string
    changeLogEntities: ChangeLogEntities
}

function FormattedValue({
    value,
    field,
    changeLogEntities,
}: FormattedValueProps) {
    const formattedValue = getFormattedValue({
        value,
        field,
        changeLogEntities,
    })

    return typeof formattedValue === 'string' ? (
        <SharedInfoText w="200px">{formattedValue}</SharedInfoText>
    ) : (
        formattedValue
    )
}

interface GetFormattedValueParams {
    value: unknown
    field: string
    changeLogEntities: ChangeLogEntities
}

/**
 * Resolves a user entity from the pre-fetched lookup map
 */
function resolveUser(
    value: unknown,
    changeLogEntities: ChangeLogEntities,
): UserFieldsFragment | undefined {
    if (typeof value !== 'string') return undefined
    return changeLogEntities.users.get(value)
}

/**
 * Resolves a client entity from the pre-fetched lookup map
 */
function resolveClient(
    value: unknown,
    changeLogEntities: ChangeLogEntities,
): ClientPreviewFieldsFragment | undefined {
    if (typeof value !== 'string') return undefined
    return changeLogEntities.clients.get(value)
}

/**
 * Resolves a customer segment entity from the pre-fetched lookup map
 */
function resolveCustomerSegment(
    value: unknown,
    changeLogEntities: ChangeLogEntities,
): CustomerSegmentPreviewFieldsFragment | undefined {
    if (typeof value !== 'string') return undefined
    return changeLogEntities.customerSegments.get(value)
}

function getFormattedValue({
    value,
    field,
    changeLogEntities,
}: GetFormattedValueParams): React.ReactNode {
    // Handle user entity fields
    if (USER_FIELD_KEYS.includes(field)) {
        const user = resolveUser(value, changeLogEntities)
        if (user) {
            return getUserFullName(user)
        }
        // If loading, show a placeholder; if not found, show the ID
        if (changeLogEntities.loading) {
            return 'Loading...'
        }
        return typeof value === 'string' ? value : 'Unknown User'
    }

    // Handle client entity fields
    if (CLIENT_FIELD_KEYS.includes(field)) {
        const client = resolveClient(value, changeLogEntities)
        if (client) {
            return getClientFullName(client) ?? 'Unknown Client'
        }
        if (changeLogEntities.loading) {
            return 'Loading...'
        }
        return typeof value === 'string' ? value : 'Unknown Client'
    }

    if (CUSTOMER_SEGMENT_FIELD_KEYS.includes(field)) {
        const customerSegment = resolveCustomerSegment(value, changeLogEntities)
        if (customerSegment) {
            return customerSegment.name
        }
        if (changeLogEntities.loading) {
            return 'Loading...'
        }
        return typeof value === 'string' ? value : 'Unknown Customer Segment'
    }

    // Handle special formatting cases

    if (field === 'max_inactivity_duration') {
        if (typeof value === 'number') {
            return value / (1000 * 60) + ' minutes'
        }
    }

    if (['name', 'title'].includes(field)) {
        return String(value)
    }

    if (CUSTOMER_SEGMENT_SPECIAL_FORMAT_KEYS.includes(field)) {
        if (!Array.isArray(value)) {
            return 'Updated'
        }

        const firstValue = value.at(0)

        if (!firstValue) {
            return 'None'
        }

        if (
            typeof firstValue === 'object' &&
            firstValue !== null &&
            'parameters' in firstValue
        ) {
            return `${value.flatMap(v => v.parameters).length} parameters`
        }

        return 'Updated'
    }

    if (CUSTOMER_SEGMENT_SPECIAL_FORMAT_CLIENT_KEYS.includes(field)) {
        return Array.isArray(value) ? String(value.length) : 'Updated'
    }

    if (HTML_FIELD_KEYS.includes(field)) {
        if (!value || typeof value !== 'string') return 'Updated'

        return (
            <SharedStack pl={2}>
                <ChangeLogHtmlItem html={value} />
            </SharedStack>
        )
    }

    // Handle complex object fields - show "Updated" instead of raw JSON
    if (COMPLEX_OBJECT_FIELD_KEYS.includes(field)) {
        return 'Updated'
    }

    // Default formatting
    return getDefaultFormatted(value)
}

function getDefaultFormatted(value: unknown): string {
    if (typeof value === 'string') {
        return getCleanEnum(value)
    }

    if (typeof value === 'number') {
        // Large numbers likely represent timestamps
        return value > 1000000000 ? format(value, 'MM/dd/yyyy') : String(value)
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
    }

    return JSON.stringify(value, null, 2)
}

/**
 * Field keys for customer segment filter objects (not entity lookups, but complex objects)
 */
export const CUSTOMER_SEGMENT_SPECIAL_FORMAT_KEYS: string[] = [
    'all_inclusions',
    'any_exclusions',
    'any_inclusions',
    'any_other_exclusions',
]

/**
 * Field keys for customer segment client arrays
 */
export const CUSTOMER_SEGMENT_SPECIAL_FORMAT_CLIENT_KEYS: string[] = [
    'included_clients',
    'excluded_clients',
]

/**
 * Field keys for notification template fields that should show "Updated" instead of raw value
 */
export const HTML_FIELD_KEYS: string[] = ['email_body']

/**
 * Field keys for complex JSON objects that should show "Updated" instead of raw JSON
 */
export const COMPLEX_OBJECT_FIELD_KEYS: string[] = [
    'form_fields',
    'adjusted_availability',
    'pms_adjusted_availability',
    'hours',
    'pms_hours',
    'availability',
    'drop_off_hours',
    'pricing',
    'recurring',
    'follow_up_settings',
    'widget_styles',
    'featured_reviews',
]